<template>
  <div class="subscription-anomaly-list">

    <v-alert v-if="!loading && totalAnomalies === 0" type="success" text>
      Aucune anomalie détectée
    </v-alert>

    <v-alert v-if="!loading && totalAnomalies > 0" type="warning" text class="mb-4">
      {{ totalAnomalies }} anomalie(s) détectée(s)
    </v-alert>

    <v-expansion-panels v-if="totalAnomalies > 0" inset>

      <template v-for="section in visibleSections">
        <v-expansion-panel :key="section.key" cols="12" md="6">
          <v-expansion-panel-header>
            <v-row>
              <v-col class="text">{{ section.label }}</v-col>
              <v-col class="text-right pr-6 font-weight-light">{{ section.items.length }}</v-col>
            </v-row>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-data-table
                dense
                :headers="section.headers"
                :items="section.items"
                hide-default-footer
                :items-per-page="-1"
            >
              <template #item.name="{item}">
                <router-link v-if="section.nameRoute" :to="section.nameRoute(item)" target="_blank">
                  {{ section.nameLabel ? section.nameLabel(item) : item.name }}
                </router-link>
                <span v-else>{{ section.nameLabel ? section.nameLabel(item) : item.name }}</span>
              </template>

              <template #item.organization.name="{item}">
                <router-link v-if="item.organization" :to="{name: 'organizations.subscriptions', params: {id: item.organization._id || item.organization.id}}" target="_blank">
                  {{ item.organization.name }}
                </router-link>
              </template>

              <template #item.amount="{item}">
                {{ item.amount.toFixed(2) }} €
              </template>

              <template #item.energyProviderKwhPrice="{value}">
                {{ value }} €
              </template>

              <template #item.energy="{item}">
                {{ (item.energy / 1000).toFixed(3) }} kWh
              </template>
            </v-data-table>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </template>

    </v-expansion-panels>
  </div>
</template>

<script>
import AnomalyRepository from "@repository/AnomalyRepository";

export default {
  props: {
    organizationId: {
      type: String,
      default: null,
    },
    expensePeriodId: {
      type: String,
      default: null,
    },
    autoLoad: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      loading: false,
      anomalies: null,
    }
  },

  mounted() {
    if (this.autoLoad) {
      this.loadData()
    }
  },

  computed: {
    sections() {
      if (!this.anomalies) return []

      const nameHeader = { text: this.$t('name'), value: 'name' }
      const orgHeader = { text: this.$t('organization.organization'), value: 'organization.name', align: 'left' }
      const amountHeader = { text: this.$t('amount'), value: 'amount', align: 'left' }
      const kwhPriceHeader = { text: this.$t('charging-point.energy-provider-kwh-price'), value: 'energyProviderKwhPrice', align: 'left' }
      const linesHeader = { text: this.$t('nb-lines'), value: 'count', align: 'left' }
      const energyHeader = { text: 'kWh', value: 'energy' }

      return [
        {
          key: 'chargingPointsWithoutLicence',
          label: 'Bornes sans licence',
          items: this.anomalies.chargingPointsWithoutLicence || [],
          headers: [nameHeader, orgHeader],
          nameRoute: (item) => ({ name: 'charging-points.detail.licences', params: { id: item._id } }),
        },
        {
          key: 'meterDevicesWithoutLicence',
          label: 'Compteurs sans licence',
          items: this.anomalies.meterDevicesWithoutLicence || [],
          headers: [nameHeader, orgHeader],
          nameRoute: (item) => ({ name: 'meter-device.detail', params: { id: item._id } }),
        },
        {
          key: 'homeChargingPointsWithoutEnergyPrice',
          label: 'Bornes sans tarification au kWh',
          items: this.anomalies.homeChargingPointsWithoutEnergyPrice || [],
          headers: [nameHeader, orgHeader],
          nameRoute: (item) => ({ name: 'charging-points.detail', params: { id: item._id } }),
        },
        {
          key: 'homeChargingPointsEnergyPriceTooBig',
          label: 'Bornes avec tarification au kWh excessive (> 0,5 €)',
          items: this.anomalies.homeChargingPointsEnergyPriceTooBig || [],
          headers: [nameHeader, orgHeader, kwhPriceHeader],
          nameRoute: (item) => ({ name: 'charging-points.detail', params: { id: item._id } }),
        },
        {
          key: 'homeMeterDevicesWithoutEnergyPrice',
          label: 'Compteurs sans tarification au kWh',
          items: this.anomalies.homeMeterDevicesWithoutEnergyPrice || [],
          headers: [nameHeader, orgHeader],
          nameRoute: (item) => ({ name: 'meter-device.detail', params: { id: item._id } }),
        },
        {
          key: 'homeMeterDevicesEnergyPriceTooBig',
          label: 'Compteurs avec tarification au kWh excessive (> 0,5 €)',
          items: this.anomalies.homeMeterDevicesEnergyPriceTooBig || [],
          headers: [nameHeader, orgHeader, kwhPriceHeader],
          nameRoute: (item) => ({ name: 'meter-device.detail', params: { id: item._id } }),
        },
        {
          key: 'homeChargingPointsWithoutExpenseSubscriptionLicence',
          label: 'Bornes à domicile sans licence de remboursement',
          items: this.anomalies.homeChargingPointsWithoutExpenseSubscriptionLicence || [],
          headers: [nameHeader, orgHeader],
          nameRoute: (item) => ({ name: 'charging-points.detail.licences', params: { id: item._id } }),
        },
        {
          key: 'homeMeterDevicesWithoutExpenseSubscriptionLicence',
          label: 'Compteurs à domicile sans licence de remboursement',
          items: this.anomalies.homeMeterDevicesWithoutExpenseSubscriptionLicence || [],
          headers: [nameHeader, orgHeader],
          nameRoute: (item) => ({ name: 'meter-device.detail', params: { id: item._id } }),
        },
        {
          key: 'memberWithNoCorporateToken',
          label: 'Membres sans badge pro',
          items: this.anomalies.memberWithNoCorporateToken || [],
          headers: [nameHeader, orgHeader],
          nameRoute: (item) => ({ name: 'member.tokens', params: { id: item.organization.id, memberId: item._id } }),
          nameLabel: (item) => this.customerLabel(item),
        },
        {
          key: 'expenseMemberWithoutIban',
          label: 'Membres sans IBAN',
          items: this.anomalies.expenseMemberWithoutIban || [],
          headers: [nameHeader, orgHeader],
          nameRoute: (item) => ({ name: 'member.edit', params: { id: item.organization.id, memberId: item._id } }),
          nameLabel: (item) => this.customerLabel(item),
        },
        {
          key: 'expenseMemberWithoutIbanAndWithOneCharge',
          label: 'Membres sans IBAN ayant une charge à domicile',
          items: this.anomalies.expenseMemberWithoutIbanAndWithOneCharge || [],
          headers: [nameHeader, orgHeader],
          nameRoute: (item) => ({ name: 'member.edit', params: { id: item.organization.id, memberId: item._id } }),
          nameLabel: (item) => this.customerLabel(item),
        },
        {
          key: 'expenseMemberWithHighAmount',
          label: 'Membres avec montant de remboursement excessif (> 400 €)',
          items: this.anomalies.expenseMemberWithHighAmount || [],
          headers: [nameHeader, orgHeader, amountHeader],
          nameRoute: (item) => ({ name: 'organizations.expense-periods.member', params: { id: item.organization.id, memberId: item._id, periodId: item.expensePeriodId } }),
          nameLabel: (item) => this.customerLabel(item),
        },
        {
          key: 'expenseMemberWithNoAmount',
          label: 'Membres avec charges et montant de remboursement à 0 €',
          items: this.anomalies.expenseMemberWithNoAmount || [],
          headers: [nameHeader, orgHeader, amountHeader],
          nameRoute: (item) => ({ name: 'organizations.expense-periods.member', params: { id: item.organization.id, memberId: item._id, periodId: item.expensePeriodId } }),
          nameLabel: (item) => this.customerLabel(item),
        },
        {
          key: 'expenseMemberTooManyCdr',
          label: 'Membres avec trop de lignes de remboursement (> 100)',
          items: this.anomalies.expenseMemberTooManyCdr || [],
          headers: [nameHeader, orgHeader, linesHeader],
          nameRoute: (item) => ({ name: 'organizations.expense-periods.member', params: { id: item.organization.id, memberId: item._id, periodId: item.expensePeriodId } }),
          nameLabel: (item) => this.customerLabel(item),
        },
        {
          key: 'expenseLineWithHighEnergy',
          label: 'Membres avec consommation élevée (charge > 100 kWh)',
          items: this.anomalies.expenseLineWithHighEnergy || [],
          headers: [nameHeader, orgHeader, energyHeader],
          nameRoute: (item) => ({ name: 'organizations.expense-periods.member', params: { id: item.organization.id, memberId: item._id, periodId: item.expensePeriodId } }),
          nameLabel: (item) => this.customerLabel(item),
        },
        {
          key: 'expenseMemberWithoutExpenseLine',
          label: 'Membres sans ligne de remboursement',
          items: this.anomalies.expenseMemberWithoutExpenseLine || [],
          headers: [nameHeader, orgHeader],
          nameRoute: (item) => ({ name: 'organizations.expense-periods.member', params: { id: item.organization.id, memberId: item._id, periodId: item.expensePeriodId } }),
          nameLabel: (item) => this.customerLabel(item),
        },
        {
          key: 'organizationsWithoutSubscription',
          label: 'Organisations sans abonnement',
          items: this.anomalies.organizationsWithoutSubscription || [],
          headers: [nameHeader],
          nameRoute: (item) => ({ name: 'organizations.subscriptions', params: { id: item._id } }),
        },
        {
          key: 'organizationWithHomeChargingPointsWithoutExpenseSubscription',
          label: 'Organisations (avec borne à domicile) sans abonnement de remboursement',
          items: this.anomalies.organizationWithHomeChargingPointsWithoutExpenseSubscription || [],
          headers: [nameHeader],
          nameRoute: (item) => ({ name: 'organizations.subscriptions', params: { id: item._id } }),
        },
        {
          key: 'expenseSubscriptionWithoutTariffType',
          label: 'Abonnements de remboursement sans type de tarification',
          items: this.anomalies.expenseSubscriptionWithoutTariffType || [],
          headers: [nameHeader, orgHeader],
          nameRoute: (item) => ({ name: 'organizations.subscriptions.edit', params: { id: item.organization._id, subscriptionId: item._id } }),
        },
        {
          key: 'expenseSubscriptionOrganizationWithoutPrice',
          label: 'Abonnements de remboursement sans tarification au kWh',
          items: this.anomalies.expenseSubscriptionOrganizationWithoutPrice || [],
          headers: [nameHeader, orgHeader],
          nameRoute: (item) => ({ name: 'organizations.subscriptions.edit', params: { id: item.organization._id, subscriptionId: item._id } }),
        },
        {
          key: 'expenseSubscriptionOrganizationEnergyPriceTooBig',
          label: 'Abonnements de remboursement avec tarification excessive au kWh (> 0,5 €)',
          items: this.anomalies.expenseSubscriptionOrganizationEnergyPriceTooBig || [],
          headers: [nameHeader, orgHeader],
          nameRoute: (item) => ({ name: 'organizations.subscriptions.edit', params: { id: item.organization._id, subscriptionId: item._id } }),
        },
        {
          key: 'corporateTokenWithWrongLink',
          label: 'Badges entreprise avec lien invalide',
          items: this.anomalies.corporateTokenWithWrongLink || [],
          headers: [{ text: 'Badge Uid', value: 'uid' }, nameHeader],
        },
        {
          key: 'freeUsageMemberGroupsWithoutOrganization',
          label: 'Groupes de membres pour usage gratuit non rattachés',
          items: this.anomalies.freeUsageMemberGroupsWithoutOrganization || [],
          headers: [nameHeader, orgHeader],
        },
        {
          key: 'privateChargeMemberGroupsWithoutOrganization',
          label: 'Groupes de membres pour charge privée non rattachés',
          items: this.anomalies.privateChargeMemberGroupsWithoutOrganization || [],
          headers: [nameHeader, orgHeader],
        },
        {
          key: 'orgPaymentGroupWithoutDeferredPayment',
          label: this.$t('anomaly.org-payment-group-without-deferred-payment'),
          items: this.anomalies.orgPaymentGroupWithoutDeferredPayment || [],
          headers: [nameHeader, orgHeader],
          nameRoute: (item) => ({ name: 'organizations.billing-info', params: { id: item.organization._id || item.organization.id } }),
        },
      ]
    },

    visibleSections() {
      return this.sections.filter(s => s.items.length > 0)
    },

    totalAnomalies() {
      return this.visibleSections.reduce((sum, s) => sum + s.items.length, 0)
    },
  },

  methods: {
    reset() {
      this.anomalies = null
      this.loading = false
    },

    loadData() {
      this.anomalies = null
      this.loading = true
      this.$emit('loading', true)
      AnomalyRepository.subscription(this.organizationId, this.expensePeriodId).then(response => {
        this.anomalies = response
        this.loading = false
        this.$emit('loading', false)
        this.$emit('loaded', { total: this.totalAnomalies, anomalies: response })
      }).catch(e => {
        this.notifyError(e)
        this.loading = false
        this.$emit('loading', false)
      })
    },

    customerLabel(item) {
      let tmp = []
      if (item.firstname) tmp.push(item.firstname)
      if (item.lastname) tmp.push(item.lastname)
      if (tmp.length === 0) {
        if (item.customer?.evcoId) {
          tmp.push(item.customer.evcoId)
        } else {
          tmp.push('no-name')
        }
      }
      return tmp.join(' ')
    },
  },
}
</script>